import { memo, useEffect, useState } from 'react';
import { AppWindow, MonitorUp } from 'lucide-react';
import { Button, Modal } from '@cyphex/ui';
import { desktopBridge, isDesktopShell } from '@/features/desktop/bridge';
import type { ScreenShareHint } from '../media/contract';
import { ToggleRow } from './settings/SettingRow';

export interface ScreenSharePickerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** `computerSound` is the "Include computer sound" switch. */
  onStart: (hint: ScreenShareHint, computerSound: boolean) => void;
}

/**
 * What "Include computer sound" means where this person is sharing from, said
 * BEFORE they turn it on.
 *
 * On the Windows desktop client the only sound there is to share is everything
 * the machine plays — this call included — so the other participants hear
 * their own voices come back while it is on. That is the echo this switch is
 * off by default to prevent, and the person turning it on is told so. A browser
 * is asked for a tab's sound only (`localMedia.captureWithSound`), which cannot
 * be this call.
 */
function soundNotice(desktop: boolean, platform: string | undefined): string {
  if (!desktop) {
    return 'Choose the browser tab that is playing the video and keep "Share tab audio" ticked. ' +
      'The sound of a whole screen or a window is not shared: it would send this call back to everyone.';
  }
  if (platform === 'win32') {
    return 'Windows shares everything your computer plays, including this call, so the others ' +
      'will hear their own voices echo back while it is on. Use it only to play a video.';
  }
  return 'The desktop app cannot share your computer’s sound on this system. Share from the ' +
    'browser to include a tab’s sound.';
}

/**
 * Cyphex picks the *encoding profile*; the SOURCE is picked in the next step.
 * `detail` = 15 fps crisp slides, `motion` = 30 fps video — the wrong hint costs
 * ~1.1 Mbps per subscriber (study §4.2).
 *
 * This dialog used to offer "Entire screen / Application window / Browser tab"
 * buttons. They were removed because nothing was wired to them: the choice was
 * discarded and whatever chooser came next decided the source anyway. A control
 * that does nothing is worse than no control, so the dialog now says plainly
 * where the choice actually happens — the desktop client's own source picker
 * (screens and windows with live thumbnails), or the browser's chooser.
 *
 * COMPUTER SOUND IS OFF EVERY TIME THE DIALOG OPENS. It used to be asked for on
 * every share, and on Windows that sent every other participant's voice back to
 * them — see `ShareStartOptions`. The room hears the presenter's microphone
 * either way; this switch is for a video's soundtrack, and it is turned on for
 * one share at a time, deliberately.
 */
export const ScreenSharePicker = memo(function ScreenSharePicker({
  open, onOpenChange, onStart,
}: ScreenSharePickerProps) {
  const [hint, setHint] = useState<ScreenShareHint>('detail');
  const [computerSound, setComputerSound] = useState(false);
  const desktop = isDesktopShell();

  useEffect(() => {
    if (open) setComputerSound(false);
  }, [open]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Share content"
      description="Choose how it should be encoded, then pick what to share."
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" leftIcon={MonitorUp} onClick={() => onStart(hint, computerSound)}>
            Continue
          </Button>
        </>
      }
    >
      <fieldset>
        <legend className="mb-2 text-base font-semibold">Optimise for</legend>
        <div className="flex gap-2">
          <Button
            variant={hint === 'detail' ? 'subtle' : 'ghost'}
            size="sm"
            onClick={() => setHint('detail')}
            aria-pressed={hint === 'detail'}
          >
            Text and slides · 15 fps
          </Button>
          <Button
            variant={hint === 'motion' ? 'subtle' : 'ghost'}
            size="sm"
            onClick={() => setHint('motion')}
            aria-pressed={hint === 'motion'}
          >
            Video and motion · 30 fps
          </Button>
        </div>
      </fieldset>

      <div className="mt-3">
        <ToggleRow
          label="Include computer sound"
          hint="Everyone hears your microphone either way. Turn this on only to play a video with its sound."
          notice={computerSound ? soundNotice(desktop, desktopBridge()?.platform) : null}
          checked={computerSound}
          onChange={setComputerSound}
        />
      </div>

      <p className="mt-3 flex items-start gap-2 rounded-lg bg-subtle p-3 text-base text-muted-foreground">
        <AppWindow size={16} className="mt-0.5 shrink-0" aria-hidden />
        <span>
          {desktop
            ? 'Next you will choose a screen or a single window from your machine, with a preview of each.'
            : 'Your browser will ask which screen, window or tab to share.'}
        </span>
      </p>
    </Modal>
  );
});
